import React, { useEffect, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import MonthDD from '../../../../utils/MonthDD';
import { createPortal } from 'react-dom';
import useSidebarSubMainTab from '../../../shared/HandleOpenSidebarSubTab/useSidebarSubMainTab';

const AreaChart = ({ data, insurancesData, PaymentReceivedDateType }) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [isHovered, setIsHovered] = useState(true);
  const hoverTimer = useRef(null); // Ref to hold the timer ID
  let currMonth = new Date().getMonth();
  let currYear = new Date().getFullYear();
  let months = [
    ...MonthDD.slice(currMonth + 1, 12).map(month => `${month} ${currYear - 1}`),
    ...MonthDD.slice(0, currMonth + 1).map(month => `${month} ${currYear}`)
  ].slice(6, 12).reverse();
  data = data?.slice(0, 6);

  const chartData = {
    options: {
      chart: {
        type: 'area',
        stacked: false,
        events: {
          dataPointMouseEnter: function (event, chartContext, config) {
            hoverTimer.current = setTimeout(() => {
              setHoveredPoint({
                position: { X: event.clientX, Y: event.clientY },
                seriesIndex: config.seriesIndex,
                dataPointIndex: config.dataPointIndex,
                w: chartContext.w,
              });
              setIsHovered(true);
            }, 50);
          },
          dataPointMouseLeave: function () {
            clearTimeout(hoverTimer.current); // Cancel the pending timeout
          },
        },
        zoom: {
          enabled: false,
        },
        contextMenu: {
          menu: false,
        },
      },
      stroke: {
        colors: ["#7BD57E"],
        width: 1
      },
      colors: ['#84D786'],
      title: {
        text: '',
        align: 'center',
      },
      markers: {
        size: 4,
        colors: ['#7BD57E'],
        strokeColors: '#7BD57E',
        strokeWidth: 1,
      },
      xaxis: {
        categories: months,
        labels: {
          style: {
            fontSize: 12,
            fontFamily: 'Poppins',
            fontWeight: '400',
            colors: '#434343',
          },
        },
      },
      yaxis: {
        // tickAmount: 6, 
        labels: {
          style: {
            fontSize: 12,
            fontFamily: 'Poppins',
            fontWeight: '400',
            colors: '#434343',
          },
          formatter: (val) => {
            return `$${String(val).length < 4 ? val : String(val).length < 7 ? val / 1000 + 'K' : String(val).length >= 7 ? val / 1000000 + 'M' : val}`;
          },
        },
        // grid: {
        //   row: {
        //     colors: ['#fff', '#f6f6f6'],
        //     opacity: 1,
        //   },
        //   padding: {
        //     top: 20,
        //     bottom: 20,
        //   },
        // },

      },
      dataLabels: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
      tooltip: {
        enabled: false, // Disable the default tooltip
        intersect: true,
        shared: false,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2, // minimum number of fractional digits
            maximumFractionDigits: 2, // maximum number of fractional digits
          });

          // Calculate total amount for the given dataPointIndex
          const totalAmount = series.reduce((sum, currSeries) => {
            return sum + (currSeries[dataPointIndex] || 0);
          }, 0);

          const formattedTotal = formatter.format(totalAmount);

          // Get the month name (assuming you have an array `months`)
          const monthName = months?.[dataPointIndex] || 'Unknown';

          // Create tooltip content
          const seriesData = w.config.series.map((item, index) => {
            let insuranceData = insurancesData?.[`${dataPointIndex}`] || [];
            let row = insuranceData?.map(it => {
              let amount = it.Amount ? formatter.format(it.Amount) : formatter.format(0);
              return `
              <div class='d-flex justify-content-between mt-1'>
                  <p class='xsm_text pe-2'>
                      ${it.InsuranceName}
                  </p> 
                  <p class='xsm_text'>${amount}</p>
              </div>`
            })
            return row.join('');
          });
          return `
        <div class="apexcharts-tooltip-title pt-0 mt-0">
         <div class="xsm_heading app_border py-2 text-center border-end-0 border-start-0 border-top-0">
          <div 
            class="d-flex justify-content-between align-items-center px-10 w-100"
            style="display: flex; justify-content: space-between; width: 100%;"
          >
            <p class="m-0 text-start flex-grow-1 pe-2">${monthName}</p>
            <strong class="m-0 text-end">${formattedTotal}</strong>
          </div>
          </div>
          <div class="content">${seriesData.join('')}</div>
        </div>`;
        },

      },
      download: false,
    },
    series: [
      {
        data: data,
        fill: {
          // colors: ['#D6FCAC'], // Set the area color
        },
      },
    ],
    // tooltip: {
    //   enabled: false, // Disable the default tooltip
    //   intersect: true,
    //   shared: false
    // },
    grid: {
      border: '0.25px solid #959595',
    },
  };

  return (
    <>
      <Chart
        options={chartData.options}
        series={chartData.series}
        type="area"
        height="193"
      />
      {hoveredPoint && isHovered && hoveredPoint.seriesIndex !== -1 && (
        createPortal(
          <CustomTooltip
            position={hoveredPoint.position}
            series={chartData.series}
            seriesIndex={hoveredPoint.seriesIndex}
            dataPointIndex={hoveredPoint.dataPointIndex}
            w={hoveredPoint.w}
            setHoveredPoint={setHoveredPoint}
            isHovered={isHovered}
            setIsHovered={setIsHovered}
            PaymentReceivedDateType={PaymentReceivedDateType}
            insurancesData={insurancesData}
            months={months}
          />,
          document.getElementById('root') // Render to the root
        )
      )}
    </>
  );
};

export default AreaChart;

const CustomTooltip = ({ position, series, seriesIndex, dataPointIndex, w, setHoveredPoint, isHovered, setIsHovered, PaymentReceivedDateType, months, insurancesData }) => {
  const openSidebarSubMainTab = useSidebarSubMainTab();
  const [isOverflowedY, setIsOverflowedY] = useState(false);
  const [isOverflowedX, setIsOverflowedX] = useState(false);

  const checkTooltipOverflow = () => {
    const tooltip = document.getElementById('tooltip');
    if (!tooltip) return
    const rect = tooltip.getBoundingClientRect();
    setIsOverflowedY(rect.bottom > window.innerHeight);
    setIsOverflowedX(rect.right > window.innerWidth);

  };

  useEffect(() => {
    checkTooltipOverflow();
    window.addEventListener('resize', checkTooltipOverflow);
    return () => window.removeEventListener('resize', checkTooltipOverflow);
  }, []);
  const seriesData = w.config.series.map((item, index) => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    // let amount = item.data?.[dataPointIndex]
    // ? formatter.format(item.data[dataPointIndex])
    // : formatter.format(0);

    let insuranceData = insurancesData?.[`${dataPointIndex}`] || [];
    //   let row = insuranceData?.map(it => { 
    //     let amount = it.Amount ? formatter.format(it.Amount) : formatter.format(0);
    //   return `
    //   <div class='d-flex justify-content-between mt-1'>
    //       <p class='xsm_text pe-2'>
    //           ${it.InsuranceName}
    //       </p> 
    //       <p class='xsm_text'>${amount}</p>
    //   </div>`
    // })
    const monthName = months?.[dataPointIndex] || 'Unknown';
    let amounts = item.data?.[dataPointIndex]
      ? formatter.format(item.data[dataPointIndex])
      : formatter.format(0);
    // const totalAmount = series.reduce((sum, currSeries) => {
    //     return sum + (currSeries[dataPointIndex] || 0);
    // }, 0);
    // const formattedTotal = formatter.format(totalAmount);
    // return row.join('');
    // if (item.name === 'Charges') {
    // } else {

    const handleNavigate = (InsuranceID) => {
      openSidebarSubMainTab(`Payments-73-{"DateType":"${PaymentReceivedDateType} ${months[dataPointIndex]}", "PostingStatus":[], "InsuranceID":"${InsuranceID}"}`)

      // const paths = {
      //   'Denied': `Account Receivables-7222-{"DateType":"${PaymentReceivedDateType} ${months[dataPointIndex]}"}`,
      //   'Billed': `Reports-413-{"DateType":"${PaymentReceivedDateType} ${months[dataPointIndex]}"}`,
      //   'Not Billed': `Submission-7211-{"DateType":"${PaymentReceivedDateType} ${months[dataPointIndex]}"}`,
      //   'Patient Payment' : `Reports-434-{"DateType":"${PaymentReceivedDateType} ${months[dataPointIndex]}"}`,
      //   'Insurance Payment': `Reports-436-{"DateType":"${PaymentReceivedDateType} ${months[dataPointIndex]}"}`,
      //   'Adjustments': `Reports-4310-{"DateType":"${PaymentReceivedDateType} ${months[dataPointIndex]}"}`,
      //   'Balance': `Reports-432-{"DateType":"${PaymentReceivedDateType} ${months[dataPointIndex]}"}`,
      // };
      // if (paths[name]) {
      //   openSidebarSubMainTab(paths[name]);
      // }
      handleClose();
    };
    if (Number(item.data?.[dataPointIndex]) > 0)
      return (<>
        {index === 0 &&
          (<div
            key={index}
            className="px-10 pt-2 pb-2px br-bottom-1 br-light d-flex justify-content-between align-items-center"
          >
            <p className='xsm_heading'>{monthName}</p>
            <p className='xsm_heading'>{amounts}</p>
          </div>)}
        {/* (insuranceData?.length > 0 && Number(item.data?.[dataPointIndex]) > 0) &&  */}
        {insuranceData?.map(it => {
          let amount = it.Amount ? formatter.format(it.Amount) : formatter.format(0);
          return (
            <>
              <div
                key={index}
                className='d-flex justify-content-between mt-1 ar-financial-tooltip-popup px-10'>
                <p className='xsm_text w-75 validation_message text-nowrap' title={it.InsuranceName}>
                  <span
                    style={{
                      display: 'inline-block',
                      backgroundColor: '#d8d8d8',
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      marginRight: '5px',
                    }}
                  ></span>
                  {it.InsuranceName}
                </p>
                <p className='xsm_text ammount-text cursor-pointer' onClick={() => { handleNavigate(it.InsuranceID); }}>{amount}</p>
              </div>
            </>
          );
        })}
      </>
      )
    return 'Hide Tooltip'
  });
  const handleClose = () => {
    setIsHovered(false);
    setHoveredPoint(null);
  }
  let tooltipDataExist = !(seriesData?.['0'] === 'Hide Tooltip');
  return (
    // <CustomUseRef onClose={handleClose} IsActivePtPayments={false}>
    <>
      {tooltipDataExist ?
        <div
          id="tooltip" className={`apexcharts-tooltip-title ar-apexcharts-tooltip-title p-0 mb-0 Payment-Received ${isOverflowedY ? 'flip' : ''} ${isOverflowedX ? 'flipX' : ''}`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => { handleClose(); }}
          style={{
            display: isHovered ? 'block' : 'none', position: 'absolute', // Absolute positioning
            top: `${position.Y - 15}px`,
            left: `${position.X - 44}px`,
            zIndex: "100",
            transition: ".15s ease all",
            opacity: 0,
            width: '270px',
          }}
        >
          <div className='content'>{seriesData}</div>
        </div> : null}
    </>
  );
};